import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/atlas/PageHeader';
import { Button, EmptyState, Skeleton, toast } from '../../ui';
import { useMailCampaigns } from '../../hooks/useMailCampaigns';
import { useProduct } from '../../contexts/ProductContext';
import { CampaignCard } from '../../components/atlas/CampaignCard';
import { CampaignWizardDrawer } from '../../components/atlas/CampaignWizardDrawer';
import { TemplatesGridEmbed } from '../../components/atlas/TemplatesGridEmbed';
import { mailCampaignsService } from '../../services/mail-campaigns.service';

type Tab = 'em-curso' | 'historico' | 'templates';

const TABS: { id: Tab; label: string }[] = [
  { id: 'em-curso', label: 'Em curso' },
  { id: 'historico', label: 'Histórico' },
  { id: 'templates', label: 'Templates' },
];

const IN_PROGRESS = new Set(['DRAFT', 'QUEUED', 'SENDING']);
const DONE = new Set(['SENT', 'FAILED']);

export default function CampanhasPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (params.get('tab') as Tab) || 'em-curso';
  const wizardParam = params.get('wizard');
  const { selectedProductId } = useProduct();

  const wizardOpen = wizardParam !== null;
  const editId =
    wizardParam && wizardParam !== 'new' && !isNaN(Number(wizardParam))
      ? Number(wizardParam)
      : null;

  function setTab(id: Tab) {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
  }

  function openWizard(id: number | 'new') {
    const next = new URLSearchParams(params);
    next.set('wizard', String(id));
    setParams(next);
  }

  function closeWizard() {
    const next = new URLSearchParams(params);
    next.delete('wizard');
    setParams(next, { replace: true });
  }

  const [historySearch, setHistorySearch] = useState('');

  const qc = useQueryClient();
  const { data, isLoading } = useMailCampaigns(selectedProductId ?? 0, {
    page: 1,
    limit: 100,
  });
  const all: any[] =
    (data as any)?.campaigns ?? (data as any)?.data ?? (Array.isArray(data) ? (data as any) : []);

  const inProgress = all.filter((c: any) => IN_PROGRESS.has(c.status));
  const historyAll = all.filter((c: any) => DONE.has(c.status));
  const history = historySearch.trim()
    ? historyAll.filter((c: any) =>
        (c.name ?? '').toLowerCase().includes(historySearch.trim().toLowerCase())
      )
    : historyAll;

  const deleteMutation = useMutation({
    mutationFn: (id: number) => mailCampaignsService.delete(id),
    onSuccess: () => {
      toast.success('Campanha excluída');
      qc.invalidateQueries({ queryKey: ['mail-campaigns'] });
    },
    onError: () => {
      toast.error('Erro ao excluir campanha');
    },
  });

  function handleDelete(campaign: { id: number; name: string; status: string }) {
    if (campaign.status === 'SENDING') {
      toast.error('Não é possível excluir uma campanha em envio');
      return;
    }
    if (
      window.confirm(
        `Excluir a campanha "${campaign.name || 'Sem nome'}"?\n\nEsta ação não pode ser desfeita.`
      )
    ) {
      deleteMutation.mutate(campaign.id);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Campanhas"
        title="Hub de campanhas"
        subtitle="Em curso para acompanhar, histórico para revisar, templates para reaproveitar."
        actions={
          <Button icon={<Plus className="w-4 h-4" />} onClick={() => openWizard('new')}>
            Nova campanha
          </Button>
        }
      />

      {/* Tab nav */}
      <nav className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`text-sm px-4 py-2 -mb-px border-b-2 transition-colors ${
              tab === t.id
                ? 'border-ink-primary text-ink-primary font-medium'
                : 'border-transparent text-ink-tertiary hover:text-ink-secondary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {/* Em curso */}
      {tab === 'em-curso' && (
        <CampaignList
          isLoading={isLoading}
          campaigns={inProgress}
          emptyTitle="Nenhuma campanha em curso"
          emptyBody="Crie uma campanha para começar."
          onClick={(id) => openWizard(id)}
          onDelete={handleDelete}
        />
      )}

      {/* Histórico */}
      {tab === 'historico' && (
        <div className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
            <input
              type="text"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder="Buscar pelo nome da campanha..."
              className="input pl-9 w-full text-sm"
            />
          </div>
          <CampaignList
            isLoading={isLoading}
            campaigns={history}
            emptyTitle="Sem histórico"
            emptyBody={
              historySearch
                ? 'Nenhuma campanha enviada bate com esse filtro.'
                : 'Campanhas enviadas aparecerão aqui. Para filtrar leads por categoria de lifecycle, abra a campanha.'
            }
            onClick={(id) => navigate(`/campanhas/${id}`)}
            onDelete={handleDelete}
          />
        </div>
      )}

      {/* Templates */}
      {tab === 'templates' && <TemplatesGridEmbed />}

      <CampaignWizardDrawer
        open={wizardOpen}
        editId={editId}
        onClose={closeWizard}
        onCompleted={() => {
          closeWizard();
          // refetch happens via react-query invalidation inside wizard
        }}
      />
    </div>
  );
}

function CampaignList({
  isLoading,
  campaigns,
  emptyTitle,
  emptyBody,
  onClick,
  onDelete,
}: {
  isLoading: boolean;
  campaigns: any[];
  emptyTitle: string;
  emptyBody: string;
  onClick: (id: number) => void;
  onDelete?: (campaign: { id: number; name: string; status: string }) => void;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={120} />
        ))}
      </div>
    );
  }
  if (campaigns.length === 0) {
    return <EmptyState title={emptyTitle} body={emptyBody} />;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {campaigns.map((c: any) => (
        <CampaignCard
          key={c.id}
          campaign={c}
          onClick={() => onClick(c.id)}
          onDelete={onDelete ? () => onDelete(c) : undefined}
        />
      ))}
    </div>
  );
}
